import { ethers } from 'ethers';
import { Seaport } from '@opensea/seaport-js';
import nftService from './opensea';

declare var window: any;

// WETH ERC20 ABI for approval functions
const WETH_ABI = [
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function balanceOf(address account) external view returns (uint256)"
];

export const makeNFTOffer = async (
  isConnected: boolean,
  tokenId: any,
  assetContractAddress: any,
  offerPrice: any,
  endDate: any
): Promise<string> => {
  if (!isConnected) throw new Error("You must be connected to make an offer.");
  if (!tokenId || !assetContractAddress) throw new Error("NFT details are incomplete.");

  const wethAddress = '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2'; // WETH ERC-720 native token address
  const protocolAddress = '0x0000000000000068f116a894984e2db1123eb395';
  const openSeaFeeAddress = '0x0000a26b00c1F0DF003000390027140000fAa719';
  const zoneValue = '0x000056F7000000EcE9003ca63978907a00FFD100';
  const zoneHashValue = '0x0000000000000000000000000000000000000000000000000000000000000000';
  // OpenSea conduit address for WETH approvals
  const openSeaConduitAddress = '0x1E0049783F008A0085193E00003D00cd54003c71';

  const generateSalt = (): string => {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return '0x' + Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const offerAmount = ethers.parseEther(offerPrice);
  const marketplaceFeeBasisPoints = 100;
  const marketplaceFee = offerAmount * BigInt(marketplaceFeeBasisPoints) / BigInt(10000);

  const provider = new ethers.BrowserProvider(window.ethereum);
  const signer = await provider.getSigner();
  const offerer = await signer.getAddress();
  const seaport = new Seaport(signer, 
    { balanceAndApprovalChecksOnOrderCreation: true, overrides: {seaportVersion: '1.6' }});

  // Check and approve WETH allowance for OpenSea conduit
  const checkAndApproveWETH = async (): Promise<string> => {
    try {
      const wethContract = new ethers.Contract(wethAddress, WETH_ABI, signer);
      
      // Get current allowance for OpenSea conduit
      const currentAllowance = await wethContract.allowance(offerer, openSeaConduitAddress);
      
      // Check if we have enough WETH balance
      const wethBalance = await wethContract.balanceOf(offerer);
      if (wethBalance < offerAmount) {
        return "Insufficient WETH balance. Please ensure you have enough WETH to make this offer.";
      }
      
      // If allowance is insufficient, approve the conduit
      if (currentAllowance < offerAmount) {
        // Approve a large amount to avoid repeated approvals
        const approvalAmount = ethers.parseEther("1000"); // Approve 1000 WETH
        const approveTx = await wethContract.approve(openSeaConduitAddress, approvalAmount);
        await approveTx.wait();
        return "WETH approval successful";
      }
      
      return "WETH allowance sufficient";
    } catch (error) {
      console.error('WETH approval error:', error);
      return "Failed to approve WETH. Please try again.";
    }
  };

  const asset = {
    tokenId: tokenId,
    tokenAddress: assetContractAddress,
  };

 let new_parameters = {
    orderType: 3,
    offerer: offerer,
    offer: [
      {
        itemType: 1,
        identifierOrCriteria: 0,
        startAmount: offerAmount.toString(),
        endAmount: offerAmount.toString(),
        token: wethAddress
      }
    ],
    consideration: [
      {
        itemType: 3,
        identifierOrCriteria: asset.tokenId,
        token: asset.tokenAddress,
        startAmount: 1,
        endAmount: 1,
        recipient: offerer,
      },
      {
        itemType: 1,
        token: wethAddress,
        identifierOrCriteria: 0,
        startAmount: marketplaceFee.toString(),
        endAmount: marketplaceFee.toString(),
        recipient: openSeaFeeAddress,
      }
    ],
    startTime: Math.floor(new Date().getTime() / 1000).toString(),
    endTime: Math.floor(new Date(endDate).getTime() / 1000),
    zone: zoneValue,
    zoneHash: zoneHashValue,
    salt: generateSalt(),
    totalOriginalConsiderationItems: 2,
    counter: 0,
    conduitKey: seaport.OPENSEA_CONDUIT_KEY,
  };

  const getSign = async (parameters: any): Promise<any> => {
    try {
      const signature = await seaport.signOrder(parameters);
      return {
        parameters: parameters,
        signature: signature,
      };
    } catch (error) {
      return "Failed to sign offer. Please try again.";
    }
  };

  const sendToApi = async (order: any): Promise<string> => {
    try {
      // Use the hybrid approach: submit signed order to our API
      const signedOrder = {
        parameters: order.parameters,
        signature: order.signature,
        protocol_address: protocolAddress
      };

      const result = await nftService.submitOffer(signedOrder);
      
      return result.message || "Offer submitted successfully!";
    } catch (error) {
      console.error('Error submitting offer:', error);
      return "Failed to submit offer. Please try again.";
    }
  };

  // First check and approve WETH allowance
  const approvalResult = await checkAndApproveWETH();
  if (approvalResult.includes("Failed") || approvalResult.includes("Insufficient")) {
    return approvalResult;
  }

  const signed = await getSign(new_parameters);
  if (typeof signed === 'string') return signed;
  return await sendToApi(signed);
};